/**
 * Tarayıcı (PWA) push aboneliği — standart Web Push (VAPID).
 *
 * Akış: servis çalışanı `/sw.js` kayıtlı → sunucunun açık anahtarı alınır →
 * `PushManager.subscribe` → uç adres + anahtarlar `api/notifications/push/subscribe`'a yazılır.
 * Sunucunun anahtarı değişirse (yeniden kurulum) eski abonelik bırakılıp yenisi açılır.
 * İzin isteği yalnız kullanıcı hareketiyle yapılabilir (Ayarlar › Bildirimler / zil menüsü);
 * oturum açılışında yalnız izin VERİLMİŞSE abonelik tazelenir.
 */
import { apiClient } from '@/shared/api/http/client';

const KEY_STORE = 'octo.push.key';
const quiet = { octo: { expectSuccess: false } } as const;

export type PushSupport = 'unsupported' | 'denied' | 'default' | 'granted';

export function pushSupport(): PushSupport {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window))
    return 'unsupported';
  if (!window.isSecureContext) return 'unsupported';
  return Notification.permission as PushSupport;
}

/** Uygulama açılışında çağrılır; push dışında görevi yok (önbellek tutmaz). */
export function registerServiceWorker() {
  if (!('serviceWorker' in navigator) || !window.isSecureContext) return;
  navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {
    /* desteklenmiyor / engellendi: uygulama push'suz çalışır */
  });
}

function b64ToBytes(b64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
  const raw = atob((b64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function deviceLabel() {
  const ua = navigator.userAgent;
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows' : /Android/.test(ua) ? 'Android' : /iPhone|iPad/.test(ua) ? 'iOS' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : '';
  return `${browser}${os ? ` · ${os}` : ''}`;
}

function bodyOf(sub: PushSubscription) {
  const j = sub.toJSON();
  return { endpoint: sub.endpoint, p256dh: j.keys?.p256dh ?? '', auth: j.keys?.auth ?? '', device: deviceLabel() };
}

async function registration() {
  if (pushSupport() === 'unsupported') return null;
  return (await navigator.serviceWorker.getRegistration('/')) ?? (await navigator.serviceWorker.register('/sw.js', { scope: '/' }));
}

/**
 * Aboneliği kurar/tazeler ve sunucuya bu kullanıcı adına yazar. `ask` true ise izin istenir
 * (kullanıcı hareketi içinden çağrılmalı). Dönüş: son izin durumu.
 */
export async function enablePush(ask = false): Promise<PushSupport> {
  let state = pushSupport();
  if (state === 'unsupported' || state === 'denied') return state;
  if (state === 'default') {
    if (!ask) return state;
    state = (await Notification.requestPermission()) as PushSupport;
    if (state !== 'granted') return state;
  }
  try {
    const reg = await registration();
    if (!reg) return 'unsupported';
    await navigator.serviceWorker.ready;
    const { publicKey } = await apiClient.get<{ publicKey: string }>('/api/notifications/push/key', quiet);
    if (!publicKey) return state;
    let sub = await reg.pushManager.getSubscription();
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(KEY_STORE);
    } catch {
      /* noop */
    }
    if (sub && stored && stored !== publicKey) {
      await sub.unsubscribe().catch(() => undefined);
      sub = null;
    }
    sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(publicKey) });
    try {
      localStorage.setItem(KEY_STORE, publicKey);
    } catch {
      /* noop */
    }
    // Her açılışta yazılır: aynı tarayıcıda kullanıcı değiştiyse abonelik yeni kullanıcıya geçer.
    await apiClient.post('/api/notifications/push/subscribe', bodyOf(sub), quiet);
  } catch {
    /* push servisi erişilemez (ör. tarayıcı ayarı): uygulama içi bildirimler çalışmaya devam eder */
  }
  return state;
}

/**
 * Bu tarayıcıdaki aboneliği sunucuda pasifler. `drop` true ise tarayıcı aboneliği de bırakılır
 * (kullanıcı masaüstü bildirimlerini kapattı); çıkışta yalnız sunucu kaydı kapanır.
 */
export async function disablePush(drop = false): Promise<void> {
  if (pushSupport() === 'unsupported') return;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    await apiClient.post('/api/notifications/push/unsubscribe', bodyOf(sub), quiet).catch(() => undefined);
    if (drop) await sub.unsubscribe().catch(() => undefined);
  } catch {
    /* noop */
  }
}
